/**
 * Modal for choosing notification deep-link destination.
 * Supports app screens, products, banners, showcase sections, categories, and subcategories.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaTimes, FaSearch, FaHome, FaBox, FaImage, FaThLarge, FaTags, FaLayerGroup } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import {
    getProducts,
    getBanners,
    getShowcaseSections,
    getCategories,
} from "../../services/ClientService";

const SIMPLE_SCREENS = [
    { value: "Home", label: "Home", icon: <FaHome /> },
    { value: "Orders", label: "Orders", icon: <FaBox /> },
    { value: "Favorites", label: "Favorites", icon: <FaTags /> },
    { value: "Cart", label: "Cart", icon: <FaBox /> },
    { value: "Profile", label: "Profile", icon: <FaHome /> },
    { value: "Notifications", label: "Notifications", icon: <FaHome /> },
];

const TABS = [
    { key: "screens", label: "Screens", icon: <FaHome /> },
    { key: "products", label: "Products", icon: <FaBox /> },
    { key: "banners", label: "Banners", icon: <FaImage /> },
    { key: "sections", label: "Sections", icon: <FaThLarge /> },
    { key: "categories", label: "Categories", icon: <FaTags /> },
    { key: "subcategories", label: "Sub Categories", icon: <FaLayerGroup /> },
];

const isValidObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || "").trim());

const NotificationNavigateTargetModal = ({ visible, onClose, onSelect, currentSelection }) => {
    const [activeTab, setActiveTab] = useState("screens");
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [error, setError] = useState("");

    const resetState = useCallback(() => {
        setActiveTab("screens");
        setSearchTerm("");
        setItems([]);
        setError("");
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!visible) resetState();
    }, [visible, resetState]);

    const filteredScreens = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return SIMPLE_SCREENS;
        return SIMPLE_SCREENS.filter((s) => s.label.toLowerCase().includes(q));
    }, [searchTerm]);

    const loadTabData = useCallback(async () => {
        if (!visible || activeTab === "screens") return;

        setLoading(true);
        setError("");
        try {
            if (activeTab === "products") {
                const q = searchTerm.trim();
                if (!q) {
                    setItems([]);
                    setLoading(false);
                    return;
                }
                const requests = [
                    getProducts({ searchTerm: q, limit: 25, skip: 0, isActive: [true, false] }),
                ];
                if (isValidObjectId(q)) {
                    requests.push(getProducts({ _id: q, limit: 1, isActive: [true, false] }));
                }
                const responses = await Promise.all(requests);
                const merged = [];
                const seen = new Set();
                responses.forEach((res) => {
                    (res?.data || []).forEach((product) => {
                        const id = product._id?.toString?.() || product._id;
                        if (!id || seen.has(id)) return;
                        seen.add(id);
                        merged.push(product);
                    });
                });
                setItems(merged);
            } else if (activeTab === "banners") {
                const res = await getBanners({});
                const q = searchTerm.trim().toLowerCase();
                const banners = (res?.data || []).filter((b) =>
                    !q || (b.bannerName || "").toLowerCase().includes(q) || String(b._id || "").includes(q)
                );
                setItems(banners);
            } else if (activeTab === "sections") {
                const res = await getShowcaseSections({ isActive: [true, false] });
                const q = searchTerm.trim().toLowerCase();
                const sections = (res?.data || []).filter((s) =>
                    !q ||
                    (s.sectionName || "").toLowerCase().includes(q) ||
                    String(s._id || "").includes(q)
                );
                setItems(sections);
            } else if (activeTab === "categories") {
                const res = await getCategories({});
                const q = searchTerm.trim().toLowerCase();
                const categories = (res?.data || []).filter((c) =>
                    !q ||
                    (c.categoryName || "").toLowerCase().includes(q) ||
                    String(c._id || "").includes(q)
                );
                setItems(categories);
            } else if (activeTab === "subcategories") {
                const res = await getCategories({ flat: true, includeSubcategories: true });
                const q = searchTerm.trim().toLowerCase();
                const all = res?.data || [];
                const parentMap = new Map(
                    all.filter((c) => !c.parentCategoryId).map((c) => [String(c._id), c.categoryName])
                );
                const subs = all
                    .filter((c) => c.parentCategoryId)
                    .map((c) => ({
                        ...c,
                        parentName: parentMap.get(String(c.parentCategoryId)) || "Category",
                    }))
                    .filter((c) =>
                        !q ||
                        (c.categoryName || "").toLowerCase().includes(q) ||
                        (c.parentName || "").toLowerCase().includes(q) ||
                        String(c._id || "").includes(q)
                    );
                setItems(subs);
            }
        } catch (err) {
            console.error("[NotificationNavigateTargetModal] load error:", err);
            setError("Failed to load items. Please try again.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab, searchTerm, visible]);

    useEffect(() => {
        if (!visible) return;
        if (activeTab === "screens") return;

        const timer = setTimeout(loadTabData, activeTab === "products" ? 400 : 0);
        return () => clearTimeout(timer);
    }, [visible, activeTab, searchTerm, loadTabData]);

    const handleSelectScreen = (screen) => {
        onSelect({
            screen,
            params: null,
            label: SIMPLE_SCREENS.find((s) => s.value === screen)?.label || screen,
        });
        onClose();
    };

    const handleSelectProduct = (product) => {
        const id = product._id?.toString?.() || product._id;
        onSelect({
            screen: "ProductDetails",
            params: { productId: id },
            label: `Product: ${product.productName || id}`,
        });
        onClose();
    };

    const handleSelectBanner = (banner) => {
        const id = banner._id?.toString?.() || banner._id;
        onSelect({
            screen: "BannerItems",
            params: { bannerId: id },
            label: `Banner: ${banner.bannerName || id}`,
        });
        onClose();
    };

    const handleSelectSection = (section) => {
        const id = section._id?.toString?.() || section._id;
        onSelect({
            screen: "SectionItems",
            params: { sectionId: id },
            label: `Section: ${section.sectionName || id}`,
        });
        onClose();
    };

    const handleSelectCategory = (category) => {
        const id = category._id?.toString?.() || category._id;
        onSelect({
            screen: "CategoryItems",
            params: { categoryId: id },
            label: `Category: ${category.categoryName || id}`,
        });
        onClose();
    };

    const handleSelectSubCategory = (sub) => {
        const subId = sub._id?.toString?.() || sub._id;
        const parentId = sub.parentCategoryId?.toString?.() || sub.parentCategoryId;
        onSelect({
            screen: "CategoryItems",
            params: { categoryId: parentId, selectedSubcategoryId: subId },
            label: `Sub Category: ${sub.categoryName || subId}`,
        });
        onClose();
    };

    const renderListItem = (key, title, subtitle, onClick) => (
        <button
            key={key}
            type="button"
            onClick={onClick}
            className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 transition-colors"
        >
            <p className="text-sm font-medium text-gray-800 truncate">{title}</p>
            {subtitle ? <p className="text-xs text-gray-400 truncate mt-0.5">{subtitle}</p> : null}
        </button>
    );

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">Navigate To (On Tap)</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Choose where customers land when they tap this notification</p>
                            </div>
                            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                                <FaTimes />
                            </button>
                        </div>

                        {currentSelection?.label ? (
                            <div className="px-5 py-2 bg-blue-50 border-b border-blue-100">
                                <p className="text-xs text-blue-600">
                                    Current: <span className="font-semibold">{currentSelection.label}</span>
                                </p>
                            </div>
                        ) : null}

                        <div className="flex flex-wrap gap-1.5 px-4 py-3 border-b border-gray-100 bg-gray-50">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => {
                                        setActiveTab(tab.key);
                                        setSearchTerm("");
                                        setItems([]);
                                    }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                        activeTab === tab.key
                                            ? "bg-blue-500 text-white"
                                            : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="px-4 py-3 border-b border-gray-100">
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={
                                        activeTab === "products"
                                            ? "Search product by name or ID..."
                                            : activeTab === "screens"
                                                ? "Filter screens..."
                                                : `Search ${TABS.find((t) => t.key === activeTab)?.label?.toLowerCase() || "items"}...`
                                    }
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            {activeTab === "products" && !searchTerm.trim() ? (
                                <p className="text-xs text-gray-400 mt-2">Type a product name or MongoDB ID to search</p>
                            ) : null}
                        </div>

                        <div className="flex-1 overflow-y-auto min-h-[240px]">
                            {activeTab === "screens" ? (
                                <div>
                                    {filteredScreens.map((screen) =>
                                        renderListItem(
                                            screen.value,
                                            screen.label,
                                            `Opens ${screen.label} screen`,
                                            () => handleSelectScreen(screen.value)
                                        )
                                    )}
                                </div>
                            ) : loading ? (
                                <div className="flex items-center justify-center py-16">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                                </div>
                            ) : error ? (
                                <p className="text-center text-sm text-red-500 py-10 px-4">{error}</p>
                            ) : items.length === 0 ? (
                                <p className="text-center text-sm text-gray-400 py-10 px-4">
                                    {activeTab === "products" && !searchTerm.trim()
                                        ? "Search for a product above"
                                        : "No items found"}
                                </p>
                            ) : (
                                <div>
                                    {activeTab === "products" &&
                                        items.map((p) =>
                                            renderListItem(
                                                p._id,
                                                p.productName || "Unnamed product",
                                                `ID: ${p._id}`,
                                                () => handleSelectProduct(p)
                                            )
                                        )}
                                    {activeTab === "banners" &&
                                        items.map((b) =>
                                            renderListItem(
                                                b._id,
                                                b.bannerName || "Unnamed banner",
                                                `ID: ${b._id}`,
                                                () => handleSelectBanner(b)
                                            )
                                        )}
                                    {activeTab === "sections" &&
                                        items.map((s) =>
                                            renderListItem(
                                                s._id,
                                                s.sectionName || "Unnamed section",
                                                `ID: ${s._id}`,
                                                () => handleSelectSection(s)
                                            )
                                        )}
                                    {activeTab === "categories" &&
                                        items.map((c) =>
                                            renderListItem(
                                                c._id,
                                                c.categoryName || "Unnamed category",
                                                `ID: ${c._id}`,
                                                () => handleSelectCategory(c)
                                            )
                                        )}
                                    {activeTab === "subcategories" &&
                                        items.map((s) =>
                                            renderListItem(
                                                s._id,
                                                s.categoryName || "Unnamed sub category",
                                                `Parent: • ${s.parentName}`,
                                                () => handleSelectSubCategory(s)
                                            )
                                        )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default NotificationNavigateTargetModal;
